import React, { FC } from 'react';
import { BlockedEmailRow } from './BlockedEmailRow';
import { BlocklistEntry } from '../../../services/BlocklistService';

export interface BlockedEmailsListProps {
    entries: BlocklistEntry[];
    totalCount: number;
    currentPage: number;
    pageSize: number;
    onDelete: (id: number) => Promise<boolean>;
    onPageChange: (page: number) => void;
    loading: boolean;
    disabled?: boolean;
}

export const BlockedEmailsList: FC<BlockedEmailsListProps> = ({
    entries,
    totalCount,
    currentPage,
    pageSize,
    onDelete,
    onPageChange,
    loading,
    disabled
}) => {
    const totalPages = Math.ceil(totalCount / pageSize);

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pages: (number | string)[] = [];

        // Always show first page
        pages.push(1);

        // Show pages around current page
        const showPages = 2; // Number of pages to show on each side of current
        const startPage = Math.max(2, currentPage - showPages);
        const endPage = Math.min(totalPages - 1, currentPage + showPages);

        // Add ellipsis after first page if needed
        if (startPage > 2) {
            pages.push('...');
        }

        // Add pages around current
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        // Add ellipsis before last page if needed
        if (endPage < totalPages - 1) {
            pages.push('...');
        }

        // Always show last page if there are multiple pages
        if (totalPages > 1) {
            pages.push(totalPages);
        }

        return (
            <div className="blocked-emails-list__pagination">
                <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1 || loading}
                    className="pagination-button"
                >
                    «
                </button>
                {pages.map((page, index) => {
                    if (page === '...') {
                        return (
                            <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                                ...
                            </span>
                        );
                    }
                    return (
                        <button
                            key={page}
                            onClick={() => onPageChange(page as number)}
                            disabled={loading}
                            className={`pagination-button ${currentPage === page ? 'active' : ''}`}
                        >
                            {page}
                        </button>
                    );
                })}
                <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || loading}
                    className="pagination-button"
                >
                    »
                </button>
            </div>
        );
    };

    if (loading && entries.length === 0) {
        return <div className="blocked-emails-list__loading">Loading...</div>;
    }

    if (!loading && entries.length === 0) {
        return <div className="blocked-emails-list__empty">No blocked email addresses yet.</div>;
    }

    return (
        <div className="blocked-emails-list">
            <div className="blocked-emails-list__header">
                <h4>Blocked Emails ({totalCount})</h4>
            </div>
            <table className="blocked-emails-list__table">
                <thead>
                    <tr>
                        <th>Email Address</th>
                        <th className="blocked-emails-list__date-column">Date Added</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {entries.map(entry => (
                        <BlockedEmailRow key={entry.id} entry={entry} onDelete={onDelete} disabled={disabled} />
                    ))}
                </tbody>
            </table>
            {renderPagination()}
        </div>
    );
};
